export type Lang = "en" | "ko";
export const DEFAULT_LANG: Lang = "en";
export const LANG_KEY = "lantern.lang";

export const dict = {
  en: {
    navExplore: "Explore",
    navStay: "My stay",
    navMe: "My info",
    homeKicker: "Stay in Philadelphia",
    homeTitle1: "A warmer way",
    homeTitle2: "to stay in Philly",
    homeLead:
      "Three homes. Pay a deposit to hold the dates. Your stay unlocks only with the booking code we email you.",
    perNight: "/ night",
    roomsBedsGuests: (rooms: number, beds: number, guests: number) =>
      `${rooms} bedroom${rooms === 1 ? "" : "s"} · ${beds} bed${beds === 1 ? "" : "s"} · up to ${guests} guests`,
    roomsBaths: (rooms: number, baths: number) =>
      `${rooms} bedroom${rooms === 1 ? "" : "s"} · ${baths} bath${baths === 1 ? "" : "s"}`,
    reviewsCount: (n: number) => `${n} review${n === 1 ? "" : "s"}`,
    reviews: "reviews",
    maxGuests: "Up to",
    guests: "guests",
    depositTodayPct: "Deposit due today (30%)",
    langEn: "English",
    langKo: "한국어",
    noMessages: "No messages yet.",
    phonePlaceholder: "+1 215 555 0100",
    hello: "Hello",
    about: "About this stay",
    approxLocation: "Approximate location",
    exactAfterCode: "The exact address is shared only after you enter your booking code.",
    terms: "House terms",
    ratings: "Ratings & reviews",
    noReviews: "No reviews yet.",
    nightsLabel: "Dates",
    prev: "Prev",
    next: "Next",
    guestsLabel: "Guests",
    guestCount: (n: number) => `${n} guest${n === 1 ? "" : "s"}`,
    nightsCount: (n: number) => `${n} night${n === 1 ? "" : "s"}`,
    depositToday: "Deposit due today",
    reserve: "Reserve",
    depositPay: "Pay deposit",
    payDepositLogin: "Sign in to pay the deposit",
    pickDates: "Choose your dates first.",
    cleaning: "Cleaning fee",
    service: "Service fee",
    promo: "Promo",
    total: "Total",
    remainingNote: (amt: string) =>
      `The remaining ${amt} is due on check-in day. The door code stays hidden until then.`,
    apply: "Apply",
    promoPlaceholder: "Promo code",
    processing: "Working…",
    loginTitle: "Come in",
    loginLead: "Google, email, or phone. No passwords. Phone sign-in still needs an email so we can send your booking code.",
    emailNeededTitle: "Add an email",
    emailNeededLead:
      "Booking details and your stay code are sent by email, even if you signed in with a phone number.",
    emailPlaceholder: "you@email.com",
    emailSaved: "Email confirmed. We’ll use this for booking updates.",
    googleContinue: "Continue with Google",
    email: "Email",
    phone: "Phone",
    sendCode: "Send code",
    confirm: "Confirm",
    myStayTitle: "My stay",
    myStayGate:
      "Stay details stay hidden until you enter the booking code from your email.",
    openStay: "Open stay",
    stayEnded: (title: string) =>
      `${title} has ended. The code will be cleared and stay details are closed.`,
    leaveReview: "Leave a review",
    reviewPlaceholder: "How was the stay?",
    submitReview: "Submit review",
    close: "Close",
    payment: "Payment",
    deposit: "Deposit",
    remaining: "Balance",
    paid: "paid",
    exactLocation: "Exact location",
    payRemaining: "Pay check-in balance",
    remainingLocked: "Balance and door code unlock on check-in day.",
    doorHidden: "The door code appears here after you pay the check-in balance.",
    doorLock: "Door lock",
    doorExplain:
      "Type this number on the keypad at the door, then press unlock. It works for this stay only. We never send the door code by email or chat — if anyone asks for it, that is not Lantern.",
    noCapture: "Please don’t screenshot or share this code.",
    chatHost: "Message your host",
    message: "Message",
    send: "Send",
    youTitle: "My info",
    loading: "Loading…",
    loadFailed: "Couldn’t load your info. Refresh and try again.",
    loginToSee: "Sign in to see your inbox, past stays, reviews, and promos.",
    signIn: "Sign in",
    inbox: "Inbox",
    pastStays: "Past stays",
    myReviews: "My reviews",
    promoCodes: "Promo codes",
    markAllRead: "Mark all read",
    noMail: "No messages.",
    noPast: "No past stays.",
    noMyReviews: "You haven’t written a review yet.",
    theme: "Theme",
    darkMode: "Dark",
    lightMode: "Light",
    language: "Language",
    logout: "Log out",
    backYou: "My info",
    installTitle: "Add to Home Screen",
    installIos: "Tap Share, then Add to Home Screen. After install you can receive push alerts.",
    installOther: "Install to keep booking alerts and your stay code closer at hand.",
    install: "Install",
    pushUnsupported: "This browser doesn’t support push.",
    pushDenied: "Notifications are blocked. Allow them in browser settings.",
    pushOn: "Push alerts on",
    pushOff: "Turn on push alerts",
    outboxTitle: "Demo outbox",
    outboxLead: "No SMTP in demo, so mail lands here. Production sends real email only.",
    successKicker: "Confirmed",
    successTitle: "Your deposit is in",
    successLead:
      "We emailed payment details, stay info, and your booking code. Enter it in My stay to open the exact address and host chat. The door code is never sent by email.",
    successCta: "Enter code in My stay",
    depositPayTitle: "Deposit",
    notFound: "That page isn’t here.",
    backExplore: "Back to explore",
    offlineTitle: "You’re offline",
    offlineBody:
      "We’ll reload stays and bookings when you’re back online. A booking code already saved on this device stays put.",
    hostNavHome: "Home",
    hostNavListings: "Stays",
    hostNavGuests: "Guests",
    hostNavRevenue: "Revenue",
    hostNavChat: "Chat",
    hostLogout: "Log out",
    hostHello: "Hello",
    revenue: "Revenue",
    upcomingStays: "Upcoming",
    bookings: "Bookings",
    upcomingCheckins: "Upcoming check-ins",
    noneUpcoming: "No upcoming stays.",
    perNightShort: "/ night",
    published: "Live",
    hidden: "Hidden",
    stayAdmin: "Stay admin",
    live: "Published",
    hiddenStay: "Hidden",
    editStay: "Edit stay",
    guestAdmin: "Guests",
    paidInFull: "Paid in full",
    codeHint: "Code ends",
    revenueAdmin: "Revenue",
    revenueLead: "Collected deposits + balances",
    noRevenue: "No revenue yet.",
    guestChat: "Guest chat",
    reply: "Reply",
    hostApp: "Host app",
    hostLead: "Separate from the guest session. Guest Google or OTP accounts cannot enter here.",
    hostSignIn: "Host sign in",
    password: "Password",
    nightlyUsd: "Nightly USD",
    cleaningUsd: "Cleaning USD",
    newDoor: "New door code (leave blank to keep)",
    showOnGuest: "Show in the guest app",
    save: "Save",
    saved: "Saved.",
    weekdays: "MTWTFSS",
    weekdaysLong: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as string[],
  },
  ko: {
    navExplore: "둘러보기",
    navStay: "내 숙소",
    navMe: "내 정보",
    homeKicker: "Stay in Philadelphia",
    homeTitle1: "필라델피아의",
    homeTitle2: "따뜻한 머무름",
    homeLead:
      "세 곳의 집. 예약금으로 자리를 잡고, 이메일로 받은 숙소예약 인증코드로만 내 숙소가 열립니다.",
    perNight: "/ 박",
    roomsBedsGuests: (rooms: number, beds: number, guests: number) =>
      `${rooms}룸 · ${beds}베드 · 최대 ${guests}명`,
    roomsBaths: (rooms: number, baths: number) => `${rooms}룸 · ${baths}욕실`,
    reviewsCount: (n: number) => `후기 ${n}`,
    reviews: "후기",
    maxGuests: "최대",
    guests: "명",
    depositTodayPct: "오늘 예약금 (30%)",
    langEn: "English",
    langKo: "한국어",
    noMessages: "아직 메시지가 없습니다.",
    phonePlaceholder: "+1 215 555 0100",
    hello: "안녕하세요",
    about: "숙소 소개",
    approxLocation: "대략적인 위치",
    exactAfterCode: "정확한 주소는 숙소예약 인증코드 입력 후에만 공개됩니다.",
    terms: "약관 · 하우스 룰",
    ratings: "평점과 리뷰",
    noReviews: "아직 후기가 없습니다.",
    nightsLabel: "대여 일수",
    prev: "이전",
    next: "다음",
    guestsLabel: "인원",
    guestCount: (n: number) => `${n}명`,
    nightsCount: (n: number) => `${n}박`,
    depositToday: "오늘 결제할 예약금",
    reserve: "예약하기",
    depositPay: "예약금 결제",
    payDepositLogin: "로그인하고 예약금 결제",
    pickDates: "날짜를 먼저 선택해 주세요.",
    cleaning: "청소비",
    service: "서비스 수수료",
    promo: "프로모션",
    total: "합계",
    remainingNote: (amt: string) =>
      `잔금 ${amt}은 체크인 당일 결제합니다. 그 전까지 도어락 코드는 공개되지 않습니다.`,
    apply: "적용",
    promoPlaceholder: "프로모션 코드",
    processing: "처리 중…",
    loginTitle: "들어오세요",
    loginLead:
      "구글, 이메일, 또는 전화 인증. 비밀번호는 쓰지 않습니다. 전화로 로그인해도 예약 코드를 보내려면 이메일이 필요합니다.",
    emailNeededTitle: "이메일을 추가하세요",
    emailNeededLead: "전화로 로그인했어도 예약 정보와 숙소예약 인증코드는 이메일로 보냅니다.",
    emailPlaceholder: "you@email.com",
    emailSaved: "이메일이 확인되었습니다. 예약 안내는 이 주소로 보냅니다.",
    googleContinue: "Google로 계속",
    email: "이메일",
    phone: "전화",
    sendCode: "인증코드 보내기",
    confirm: "확인",
    myStayTitle: "내 숙소",
    myStayGate: "이메일로 받은 숙소예약 인증코드를 입력하기 전에는 숙소 정보가 표시되지 않습니다.",
    openStay: "숙소 열기",
    stayEnded: (title: string) => `${title} 숙박이 종료되었습니다. 코드를 삭제하고 상세 정보는 닫습니다.`,
    leaveReview: "후기를 남겨 주세요",
    reviewPlaceholder: "머물렀던 이야기를 적어 주세요",
    submitReview: "후기 등록",
    close: "닫기",
    payment: "결제 정보",
    deposit: "예약금",
    remaining: "잔금",
    paid: "완료",
    exactLocation: "상세 위치",
    payRemaining: "체크인 결제하기",
    remainingLocked: "잔금과 도어락 코드는 체크인 당일부터 열립니다.",
    doorHidden: "체크인 잔금을 결제하면 여기에 도어락 번호가 나타납니다.",
    doorLock: "도어락",
    doorExplain:
      "현관 키패드에 이 번호를 입력한 뒤 열림을 누르세요. 이번 숙박에만 쓸 수 있습니다. 도어락 번호는 이메일이나 채팅으로 보내지 않습니다. 번호를 묻는 연락은 사칭이니 무시하세요.",
    noCapture: "화면 캡처·공유를 삼가 주세요.",
    chatHost: "호스트와 1:1 채팅",
    message: "메시지",
    send: "전송",
    youTitle: "내 정보",
    loading: "불러오는 중…",
    loadFailed: "내 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.",
    loginToSee: "로그인하면 받은 편지함, 지난 숙박, 내 리뷰와 프로모션을 볼 수 있습니다.",
    signIn: "로그인",
    inbox: "받은 편지함",
    pastStays: "지난 숙박",
    myReviews: "내 리뷰",
    promoCodes: "프로모션 코드",
    markAllRead: "모두 읽음",
    noMail: "편지가 없습니다.",
    noPast: "지난 숙박이 없습니다.",
    noMyReviews: "작성한 리뷰가 없습니다.",
    theme: "테마",
    darkMode: "블랙 모드",
    lightMode: "화이트 모드",
    language: "언어",
    logout: "로그아웃",
    backYou: "내 정보",
    installTitle: "홈 화면에 설치",
    installIos: "공유 버튼에서 '홈 화면에 추가'를 누르면 앱처럼 쓸 수 있고, 설치 후 푸시도 받을 수 있습니다.",
    installOther: "설치하면 예약 알림과 숙소예약 인증코드를 더 안전하게 보관할 수 있습니다.",
    install: "설치",
    pushUnsupported: "이 브라우저는 푸시를 지원하지 않습니다.",
    pushDenied: "알림이 차단되어 있습니다. 브라우저 설정에서 허용해 주세요.",
    pushOn: "푸시 알림 수신 중",
    pushOff: "푸시 알림 켜기",
    outboxTitle: "데모 아웃박스",
    outboxLead: "SMTP가 없어 이 화면에 메일을 표시합니다. 운영에서는 실제 메일만 발송됩니다.",
    successKicker: "Confirmed",
    successTitle: "예약금이 확인되었습니다",
    successLead:
      "숙소 정보와 숙소예약 인증코드를 이메일로 보냈습니다. 앱의 내 숙소에 코드를 입력해야 상세 위치와 호스트 채팅이 열립니다. 도어락 코드는 메일로 보내지 않습니다.",
    successCta: "내 숙소에서 코드 입력",
    depositPayTitle: "예약금 결제",
    notFound: "그 페이지는 없습니다.",
    backExplore: "둘러보기로",
    offlineTitle: "연결이 없습니다",
    offlineBody:
      "네트워크가 돌아오면 숙소와 예약을 다시 불러옵니다. 이미 입력한 숙소예약 인증코드는 기기에 남아 있습니다.",
    hostNavHome: "홈",
    hostNavListings: "숙소",
    hostNavGuests: "고객",
    hostNavRevenue: "매출",
    hostNavChat: "채팅",
    hostLogout: "로그아웃",
    hostHello: "안녕하세요",
    revenue: "매출",
    upcomingStays: "예정 숙박",
    bookings: "예약",
    upcomingCheckins: "다가오는 체크인",
    noneUpcoming: "예정된 숙박이 없습니다.",
    perNightShort: "/박",
    published: "공개",
    hidden: "비공개",
    stayAdmin: "숙소 관리",
    live: "공개 중",
    hiddenStay: "숨김",
    editStay: "숙소 정보 변경",
    guestAdmin: "고객 관리",
    paidInFull: "전액 결제",
    codeHint: "코드 끝자리",
    revenueAdmin: "매출 관리",
    revenueLead: "결제 확인된 예약금 + 잔금",
    noRevenue: "아직 매출이 없습니다.",
    guestChat: "고객 채팅",
    reply: "답장",
    hostApp: "호스트 앱",
    hostLead: "게스트 세션과 분리된 인증입니다. 게스트 Google/OTP 계정으로는 들어올 수 없습니다.",
    hostSignIn: "호스트 로그인",
    password: "비밀번호",
    nightlyUsd: "박 요금 USD",
    cleaningUsd: "청소비 USD",
    newDoor: "새 도어락 코드 (비워두면 유지)",
    showOnGuest: "게스트 앱에 공개",
    save: "저장",
    saved: "저장했습니다.",
    weekdays: "월화수목금토일",
    weekdaysLong: ["월", "화", "수", "목", "금", "토", "일"] as string[],
  },
} as const;

export type MessageKey = {
  [K in keyof typeof dict.en]: (typeof dict.en)[K] extends string ? K : never;
}[keyof typeof dict.en];

export function readLang(raw?: string | null): Lang {
  return raw === "ko" ? "ko" : DEFAULT_LANG;
}

export function monthLabel(year: number, month: number, lang: Lang) {
  return new Date(year, month, 1).toLocaleString(lang === "ko" ? "ko-KR" : "en-US", {
    month: "long",
    year: "numeric",
  });
}

export const copy = dict[DEFAULT_LANG];
